import http from "@services/httpService"
import { L, LNotification } from "@lib/abpUtility"
import { notifyError, notifyInfo, notifySuccess } from "@lib/helper"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import { BookingModel } from "@models/booking/bookingModel"
import {
  BookingCustomerLookupModel,
  type IBookingCustomerLookupOption,
} from "@models/booking/bookingCustomerLookupModel"
import { downloadFile } from "@lib/helperFile"
import dayjs from "dayjs"

// Maps BE propertyName → label đang dùng trên form create/update
// Gọi là function để L() chạy lúc fetch (ABP localization đã ready), không phải lúc module load
// Khi thêm field mới ở BE, chỉ cần thêm dòng tương ứng vào đây
const getBookingAuditFieldLabels = (): Record<string, string> => ({
  // Booking fields
  Code: L("BOOKING_DETAIL_CODE"),
  Status: L("BOOKING_DEPOSIT_BOOKING_STATUS"),
  PaymentStatus: L("BOOKING_DETAIL_PAYMENT_SCHEDULE"),
  SalesPolicyId: L("BOOKING_DETAIL_SALE_POLICY"),
  CartId: L("BOOKING_DETAIL_CART"),
  BookingDate: L("BOOKING_DETAIL_BOOKING_DATE"),
  ExpiredDate: L("BOOKING_DETAIL_EXPIRED_DATE"),
  UnitId: L("BOOKING_DETAIL_PRODUCT"),
  AgencyMasterId: L("CONTRACT_BOOKING_DETAIL_AGENCY_MASTER"),
  StaffId: L("CONTRACT_BOOKING_CREATE_NV_PHU_TRACH"),
  Description: L("BOOKING_DETAIL_DESCRIPTION"),
  IsActive: L("STATUS"),
  // Customer fields (audit log include cả thông tin khách hàng)
  FullName: L("CUSTOMER_DETAIL_FULL_NAME"),
  IdentifierCardNumber: L("CUSTOMER_DETAIL_INDENTIFIER_CARD_NUMBER"),
  IdIssuedDate: L("CUSTOMER_DETAIL_ID_ISSUED_DATE"),
  IdIssuedPlace: L("CUSTOMER_DETAIL_ID_ISSUED_PLACE"),
})

class BookingService {
  public async create(body: any) {
    const res = await http.post("api/services/app/Booking/Create", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async update(body: any) {
    const res = await http.put("api/services/app/Booking/Update", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  /** Xoá booking nháp (FB-005). Backend chỉ cho phép khi booking chưa gửi duyệt. */
  public async delete(id: number) {
    const res = await http.delete("api/services/app/Booking/Delete", {
      params: { id },
    })
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("DELETE_SUCCESSFULLY")
    )
    return res.data
  }

  public async activateOrDeactivate(id: number, isActive) {
    const res = await http.post(
      "api/services/app/Booking/Active",
      { id },
      { params: { isActive } }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data
  }

  public async bulkActive(id: number[]) {
    const res = await http.post("api/services/app/Booking/BulkActive", id)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data
  }
  public async getBookingIdByUniqueId(uniqueId: any) {
    const res = await http.get(
      "api/services/app/Booking/GetBookingIdByUniqueId",
      {
        params: { uniqueId },
      }
    )
    return res.data.result
  }
  public async get(id: number, isViewFull?: boolean): Promise<any> {
    if (!id) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get("api/services/app/Booking/Get", {
      params: { id, isViewFull },
    })
    const { result } = res.data
    return BookingModel.assign(result)
  }

  public async getOverview(): Promise<any> {
    const res = await http.get("api/services/app/Booking/GetOverview", {})
    return res.data.result
  }

  public async getAll(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Booking/GetAll", { params })
    return res.data.result
  }

  public async getMyBookings(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Booking/GetMyBooking", {
      params,
    })
    return res.data.result
  }

  public async getAuditLog(id: any) {
    const res = await http.get("api/services/app/Booking/GetAuditLogs", {
      params: { id },
    })
    const labels = getBookingAuditFieldLabels()
    const result = (res.data.result || []).map((row: any) => {
      ; (row.items || []).forEach((item: any) => {
        item.propertyName = labels[item.propertyName] ?? item.propertyName
      })
      return row
    })
    return result
  }
  public async getListBookingHistories(bookingId: any) {
    const res = await http.get(
      "api/services/app/Booking/GetListBookingHistories",
      {
        params: { bookingId },
      }
    )
    return res.data.result
  }
  public async updateUnitForBooking(body: any) {
    const res = await http.put(
      "api/services/app/Booking/UpdateUnitForBooking",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data.result
  }
  //Floor map
  public async getListBookingForInqueue(params: any) {
    const res = await http.get(
      "api/services/app/Booking/GetListBookingForInqueue",
      {
        params,
      }
    )
    return res.data.result
  }
  public async getListStackingPlanByTower(params: any) {
    const res = await http.get(
      "api/services/app/Booking/GetListStackingPlanByTower",
      {
        params,
      }
    )
    return res.data.result
  }
  public async getUnitDetail(params: any) {
    const res = await http.get("api/services/app/Booking/GetUnitDetail", {
      params,
    })
    return res.data.result
  }

  public async changePriorityBooking(body: any) {
    const res = await http.put(
      "api/services/app/Booking/UpdateBookingPriorityInCart",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }
  // NLG sync booking
  public async startSync(id: any) {
    const res = await http.post("api/services/app/Booking/StartSync", { id })
    notifySuccess(LNotification("SUCCESS"), LNotification("SYNC_SUCCESSFULLY"))
    return res.data
  }

  public async exportTemplateBooking(params: any) {
    const res = await http.get("api/Export/ExportBookingImportTemplate", {
      params,
      responseType: "blob",
    })
    downloadFile(
      res.data,
      `EXPORT-BOOKING-IMPORT-TEMPLATE-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
  }

  public async validateBookingImport(file: File) {
    try {
      const formData = new FormData()
      formData.append("file", file)
      const res = await http.post(
        "api/Imports/Booking/ValidateBeforeImportBooking",
        formData
      )
      return res.data.result
    } catch (error) {
      notifyError(L("ERROR"), L("FORMAT_ERROR"))
      throw error
    }
  }

  public async importBooking(file: File) {
    const formData = new FormData()
    formData.append("file", file)
    const res = await http.post("api/Imports/Booking/ImportBooking", formData)
    notifyInfo(LNotification("PROCESSING"), "Đang xử lý...")
    return res.data.result
  }

  // Generate booking
  public async generateBooking(body: any) {
    const res = await http.post(
      "api/services/app/Booking/GenerateBooking",
      body
    )
    return res.data.result
  }

  public async exportTemplateBookingRepresentative(params: any) {
    const res = await http.get("api/Export/ExportBookingImportCustomerRepresentationTemplate", {
      params,
      responseType: "blob",
    })
    downloadFile(res.data, `EXPORT-BOOKING-IMPORT-TEMPLATE-REPRESENTATIVE-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`)
  }

  public async importTemplateBookingRepresentative(file: File) {
    const formData = new FormData()
    formData.append("file", file)
    const res = await http.post("api/Imports/Booking/ImportBookingCustomerRepresentation", formData)
    return res.data.result
  }

  public async validateBookingImportRepresentative(file: File) {
    try {
      const formData = new FormData()
      formData.append("file", file)
      const res = await http.post("api/Imports/Booking/ValidateBeforeImportBookingCustomerPresentation", formData)
      return res.data.result
    } catch (error) {
      notifyError(L("ERROR"), L("FORMAT_ERROR"))
      throw error
    }
  }

  public async updateGenerateBooking(body: any) {
    const res = await http.put(
      "api/services/app/Booking/UpdateGenerateBooking",
      body
    )
    return res.data.result
  }

  public async getListHistoryPrint(params: any) {
    const res = await http.get(
      "api/services/app/Booking/GetListHistoryGenerateBooking",
      { params }
    )
    return res.data.result
  }

  /*
  Generate Unit Booking Document
  * body
  * {
  *   "cartId": 0,
  *   "id": 0,
  *   "templateId": 0
  *  }
  */
  public async generateUnitBookingDocument(body: any) {
    const res = await http.post(
      "api/services/app/Booking/GenerateUnitBookingDocument",
      body

    )
    return res.data.result
  }

  // Feature-0201: danh sách chọn khách hàng khi tạo/sửa booking. Endpoint union tự
  // quyết phạm vi theo OU của token (sàn chỉ thấy KHTN của mình; nội bộ thấy cả KH
  // hiện hữu). Input là complex type nên POST body, không phải query param.
  public async getCustomerLookup(body: {
    keyword?: string
    agencyMasterId?: number
    customerType?: number
    sources?: number[]
    isViewFull?: boolean
    skipCount?: number
    maxResultCount?: number
  }): Promise<PagedResultDto<IBookingCustomerLookupOption>> {
    const res = await http.get(
      "api/services/app/BookingCustomerLookup/GetAll",
      { params: { isViewFull: true, skipCount: 0, maxResultCount: 20, ...body } }
    )
    const { result } = res.data
    return {
      totalCount: result?.totalCount ?? 0,
      items: BookingCustomerLookupModel.assigns(result?.items),
    }
  }
}

export default new BookingService()
